import { useState } from "react";
import { Button } from "../ui/Button";
import { Input, TextArea } from "../ui/Field";
import { Drawer } from "../ui/Overlay";
import { Notice } from "../ui/Notice";
import { I18nGrid } from "./I18nGrid";
import { BASE_LOCALE, filledOf, localeOf, localesOf, missingLocales, type I18nText } from "../../lib/locales";
import { contentLocaleOf, useI18n } from "../../lib/i18n";

// 类型与工具从 lib/locales 走；这里保留 re-export，调用点不用改 import
export { LOCALE_CATALOG, DEFAULT_LOCALES, BASE_LOCALE, localeOf, pickText, missingLocales, localesOf } from "../../lib/locales";
export type { I18nText, LocaleDef } from "../../lib/locales";
/** @deprecated 语言不再是写死的三种，用 DEFAULT_LOCALES / localesOf */
export { DEFAULT_LOCALES as LOCALES } from "../../lib/locales";

/**
 * 单字段的多语言输入。
 *
 * 行内只放一种语言，其余进抽屉里的 I18nGrid 平铺编辑。
 * 原来是行内展开三行输入框：语言一多就把表格行撑到几百像素。
 *
 * 行内这一种跟后台界面语言走（界面简中 → zh-CN）。原先固定放 en：界面切成中文，
 * 整张表照样一片英文，中文明明填了也看不见（2026-10-16 天赋树页）。
 * en 仍是发布必填：行内不是 en 时，en 缺了按钮变红写「缺 EN」，否则没人发现；
 * 行内语言没填时占位显示 en 的值——客户端缺这种语言拿到的就是它。
 */
export function I18nField({
  value, onChange, disabled, width = 220, label, multiline, rows, optional, countLocales,
}: {
  value: I18nText;
  onChange: (v: I18nText) => void;
  disabled?: boolean;
  width?: number;
  /** 抽屉标题里的对象名，如「卡包 3」 */
  label?: string;
  /** 长文本（个人介绍这类）：行内与抽屉里都换成多行输入框 */
  multiline?: boolean;
  rows?: number;
  /**
   * 整个字段可以不填（如亲密度每级的解锁文案）：规则是「填了任一语言，en 才必填」。
   * 一个字都没写时占位写「选填」、不显示缺几种语言——原先照样写「English（必填）」外加红色缺语言数，
   * 运营会以为不填就发布不了。
   */
  optional?: boolean;
  /**
   * 给了就把按钮写成「已填 x/N」，只按这几种语言算（物品资料页传 4 种，产品 [521]）。
   * 不给照旧写「多语言 缺几种」——那个数按全部默认语言算，含 ko / th，只给某几种语言把关的页面上看不懂。
   */
  countLocales?: readonly string[];
}) {
  const { t, lang } = useI18n();
  const [open, setOpen] = useState(false);
  const [locales, setLocales] = useState<string[]>(() => localesOf(value));
  const inline = contentLocaleOf(lang);
  const blank = optional && !Object.values(value).some((v) => v?.trim());
  const missing = blank ? [] : missingLocales(value, locales).filter((x) => x !== BASE_LOCALE && x !== inline);
  const enMissing = !blank && inline !== BASE_LOCALE && !value[BASE_LOCALE]?.trim();
  const enText = value[BASE_LOCALE]?.trim();
  const ph = inline === BASE_LOCALE
    ? t(blank ? "i18nField.enOptional" : "i18nField.enRequired")
    : enText ? `EN: ${enText}` : t("i18nField.localeEmpty", { lang: localeOf(inline).label });
  const field = {
    value: value[inline] ?? "",
    disabled,
    placeholder: ph,
    onChange: (v: string) => onChange({ ...value, [inline]: v }),
  };

  return (
    <div style={{ width }} className="flex flex-col gap-1">
      <div className={`flex gap-1.5 ${multiline ? "items-start" : "items-center"}`}>
        <span className={`flex-none text-xs text-ink-secondary ${multiline ? "pt-1.5" : ""}`} title={localeOf(inline).label}>
          {localeOf(inline).short}
        </span>
        {multiline ? <TextArea {...field} rows={rows} /> : <Input {...field} />}
        <Button size="sm" className="flex-none" tone={enMissing ? "danger" : "default"} onClick={() => setOpen(true)}>
          {countLocales ? t("i18n.filledOf", filledOf(value, countLocales)) : <>{t("i18n.editBtn")}{missing.length ? ` ${missing.length}` : ""}</>}
          {enMissing ? ` ${t("i18nField.enMissing")}` : ""}
        </Button>
      </div>

      {open && (
        <Drawer
          title={label ? t("i18n.drawerTitle", { name: label }) : t("i18n.editBtn")}
          width={620}
          onClose={() => setOpen(false)}
          extra={<Button tone="primary" onClick={() => setOpen(false)}>{t("common.done")}</Button>}
        >
          <I18nGrid
            locales={locales}
            fields={[{ key: "text", label: t("i18n.nameField"), multiline }]}
            editable={!disabled}
            value={(id) => value[id] ?? ""}
            onChange={(id, _f, v) => onChange({ ...value, [id]: v })}
            onAddLocale={(id) => setLocales([...locales, id])}
            onRemoveLocale={(id) => {
              setLocales(locales.filter((x) => x !== id));
              const next = { ...value };
              delete next[id];
              onChange(next);
            }}
          />
          <div className="mt-3">
            <Notice tone="info">{t("i18n.notice")}</Notice>
          </div>
        </Drawer>
      )}
    </div>
  );
}
